/* eslint-disable react/require-default-props */
import React, {useEffect, useState} from 'react';
import {TouchableOpacity, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';

import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import candidateReminderTestIDs from '/constant/TestIDs/CandidateReminder';
import {getSfmcUserDataFromStorage} from '/redux/action/sfmc.action';
import testProperties from '/util/testing/testProperties';

import {CheckedInReminder} from '../CheckedInReminder';
import {
  convertISOtoDateTimeAMPM,
  formatTime,
  ReminderData,
} from '../ReminderUtils/ReminderUtils.util';

import styles from './CheckInReminder.style';
interface CheckInReminderProps {
  title?: string;
  buttonText?: string;
  candidateId?: string;
  candidateName?: string;
  updateCheckInReminder?: (dataToBeSaved: any) => void;
  coachEmail?: string;
  reminderDetails?: ReminderData;
  reminderStatus?: string;
  hasReminderDetails?: boolean;
}

type ReminderDataState = ReminderData | null;

const CheckInReminder = ({
  title = 'Check-in reminder',
  buttonText = ' Add a check-in reminder',
  candidateId,
  coachEmail = '',
  reminderDetails,
  updateCheckInReminder = () => {},
  reminderStatus,
  hasReminderDetails,
}: CheckInReminderProps) => {
  const navigation = useNavigation();
  const [reminderData, setReminderData] = useState<ReminderDataState | null>(
    null,
  );
  const {
    frequency = 'Does Not Repeat',
    checkState = false,
    formattedStartDate = '',
    formattedTime = '',
    checkDate = '',
    startDate = '',
    endDate = '',
  } = reminderData || {};

  useEffect(() => {
    if (reminderDetails?.startDate) {
      setReminderData({
        ...reminderDetails,
        formattedStartDate: convertISOtoDateTimeAMPM(
          new Date(reminderDetails?.startDate),
          new Date(reminderDetails?.startDate),
        ).formattedDate,
        formattedTime: formatTime(reminderDetails?.startDate),
        formattedEndDate: convertISOtoDateTimeAMPM(
          new Date(reminderDetails?.endDate ?? ''),
          new Date(reminderDetails?.endDate ?? ''),
        ).formattedDate,
        time: reminderDetails?.startDate,
      });
    }
  }, [reminderDetails]);

  const getCrmContactIdByUsername = async (
    username: string,
  ): Promise<string | null> => {
    try {
      const data = await getSfmcUserDataFromStorage();
      if (data) {
        const userData = typeof data === 'string' ? JSON.parse(data) : data;
        if (Array.isArray(userData)) {
          const userEntry = userData.find(entry => entry[username]);
          return userEntry ? userEntry[username] : null;
        } else {
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const handleDataReceived = async (data?: ReminderData) => {
    if (!data) {
      setReminderData(null);
      return;
    }

    try {
      const retrievedCrmContactId = await getCrmContactIdByUsername(coachEmail);
      if (!retrievedCrmContactId) {
        return;
      }
      const {startDate = '', time = '', endDate = ''} = data;

      const formattedStart = convertISOtoDateTimeAMPM(
        new Date(startDate),
        new Date(time),
      );
      const formattedEnd =
        endDate && convertISOtoDateTimeAMPM(new Date(endDate), new Date(time));
      // Update reminder data
      setReminderData({
        ...data,
        formattedStartDate: formattedStart?.formattedDate,
        formattedTime: formattedStart.formattedTime,
        formattedEndDate: formattedEnd?.formattedDate,
        time: startDate,
      });
    } catch (error) {}
  };

  const onToggleClick = async (newCheckedState: boolean) => {
    if (reminderData) {
      const updatedReminderData = {
        ...reminderData,
        checkState: newCheckedState,
      };
      if (reminderData.frequency === 'Does Not Repeat') {
        setReminderData(null);
      } else {
        setReminderData(updatedReminderData);
      }
      const updateReminder = {
        checkState: updatedReminderData.checkState,
      };
      updateCheckInReminder({
        reminderData: updateReminder,
        candidateId,
      });
    }
  };

  const onReminderClicked = () => {
    if (reminderData) {
      const initialReminderData = reminderData;
      navigation.navigate('edit-checkin-reminder-screen', {
        onDataReceived: handleDataReceived,
        initialData: initialReminderData,
        candidateId,
      });
    }
  };

  const isReminderDataEmpty = (data: object | null | undefined) => {
    return !data || Object.keys(data).length === 0;
  };
  return (
    <>
      {hasReminderDetails &&
      !isReminderDataEmpty(reminderData) &&
      reminderStatus === 'A' ? (
        <CheckedInReminder
          onToggleClick={onToggleClick}
          onReminderClick={onReminderClicked}
          candidateId={candidateId}
          date={formattedStartDate}
          time={formattedTime}
          checkState={checkState}
          checkDate={checkDate}
          frequency={frequency}
          startDate={startDate}
          endDate={endDate}
        />
      ) : (
        <View
          testID={candidateReminderTestIDs.checkInReminderView}
          style={styles.mainContainer}>
          <Text
            variant="heading"
            isBold={true}
            testID={candidateReminderTestIDs.ReminderTitle}>
            {title}
          </Text>
          <View style={styles.container}>
            <TouchableOpacity
              testID={candidateReminderTestIDs.addReminderButton}
              style={styles.buttonContainer}
              onPress={() =>
                navigation.navigate('add-checkin-reminder-screen', {
                  onDataReceived: handleDataReceived,
                  candidateId,
                })
              }
              {...testProperties(
                candidateReminderTestIDs.addReminderButton,
                true,
              )}>
              <View style={styles.button}>
                <Text
                  variant="button"
                  isBold={true}
                  style={styles.buttonText}
                  {...testProperties(
                    candidateReminderTestIDs.addReminderButtonLabel,
                  )}>
                  {buttonText}
                </Text>
                <Icon name={'plus'} style={styles.plusIcon} />
                {/* Add the icon */}
              </View>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </>
  );
};

export default CheckInReminder;
